import { RiskSeverity, RiskStatus } from '@/types';

/**
 * Per-web-app severity tally. Keyed by single-char RiskSeverity codes
 * (C/H/M/L/I). Exposure ('E') is intentionally excluded — it is not a
 * severity tier we surface in this widget (mirrors the remediation tile,
 * which skips 'E').
 */
export type SeverityCounts = Record<
  Exclude<RiskSeverity, RiskSeverity.Exposure>,
  number
>;

export interface WebAppSeverityCard {
  /** Stable React key (the seed's `key`, e.g. `#webapplication#<url>`). */
  webAppKey: string;
  /** Display label (primary_url / name). Rendered as plain JSX text. */
  name: string;
  counts: SeverityCounts;
  /** Sum of counts (donut center value). */
  total: number;
}

/** Severity tiers shown in a card's legend / counted in the tally. */
export const COUNTED_SEVERITIES: Array<
  Exclude<RiskSeverity, RiskSeverity.Exposure>
> = [
  RiskSeverity.Critical,
  RiskSeverity.High,
  RiskSeverity.Medium,
  RiskSeverity.Low,
  RiskSeverity.Info,
];

/** A web application root paired with the risks that hang off it. */
export interface WebAppWithRisks {
  webAppKey: string;
  name: string;
  /**
   * The web application's own asset status (`A`/`AL`/`AP`/`AH` = Active,
   * `F`/`FR` = Frozen, `D` Deleted, `P` Pending, `U` Unavailable). WebApplication
   * embeds Asset, so it carries the asset lifecycle status. Used to drop
   * non-active web apps (see {@link isActiveWebApp}). `undefined` if absent.
   */
  assetStatus?: string;
  /**
   * Raw risk-like rows; only `status` is consumed by the pivot. `key` is used
   * upstream to dedupe risks that recur across the OPTIONAL-relationship rows
   * Neo4j returns for the same web app (see {@link adaptRowsToWebApps}).
   */
  risks: Array<{ key?: string; status?: string }>;
}

/**
 * A web app is shown only if it is ACTIVE — its asset status begins with `A`
 * (`A`, `AL`, `AP`, `AH`). This drops Frozen (`F`/`FR`), Deleted (`D`),
 * Pending (`P`), and Unavailable (`U`) web apps. A missing status is treated as
 * active (defensive: real nodes always carry a status, so this only affects
 * synthetic/edge rows we'd rather not silently hide).
 */
export function isActiveWebApp(assetStatus: string | undefined): boolean {
  if (!assetStatus) return true;
  return assetStatus.startsWith('A');
}

function emptyCounts(): SeverityCounts {
  return {
    [RiskSeverity.Critical]: 0,
    [RiskSeverity.High]: 0,
    [RiskSeverity.Medium]: 0,
    [RiskSeverity.Low]: 0,
    [RiskSeverity.Info]: 0,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Web-app subtype buckets the `node.result` of a WebApplication root can
 * land in. `webapplications` is the canonical bucket for a `:WebApplication`
 * seed root (confirmed against `useMy.ts` `handleGraphQueryResponse`
 * `case 'seed' | 'webapplication'`, which both read `graphResponse.webapplications`).
 * `assets` is included defensively for the same reason `flattenAssetGraphResponse`
 * folds WebApplication under the asset union — a generic asset-bucketed root
 * still resolves. First non-empty bucket wins.
 */
const WEB_APP_RESULT_BUCKETS = ['webapplications', 'assets'] as const;

/**
 * T0-VERIFICATION POINT — swappable adapter.
 *
 * The wire-shape is now DERIVED from the canonical consumer of the identical
 * `HAS_VULNERABILITY → Risk` tree query, not guessed:
 *   - `TreeNode` in `@/types` — `{ node: { result, relationships } }`.
 *   - `ApplicationTab.tsx` — reads `root.node.result.applications[0]` and each
 *     `riskRel.target.result.risks` off `node.relationships[]`.
 *   - `transformTreeNodeToFlat` (queryBuilder `extractQueryResults.ts`) — reads
 *     `tree.node.result` (first array bucket) + `rel.target.result.<bucket>`.
 * For a `:WebApplication` seed root, `node.result` buckets into
 * `webapplications` (see `WEB_APP_RESULT_BUCKETS`); risks hang off
 * `node.relationships[].target.result.risks` on `HAS_VULNERABILITY` edges.
 *
 * RECOMMENDED: still spot-check against a live `/my?tree=true&label=seed`
 * response in the PR — but the shape is no longer a fabrication.
 *
 * This adapter is the ONLY place that depends on that shape. If a real
 * response disagrees, change THIS function and nothing else: the
 * pivot/sort/card logic below is shape-agnostic and consumes
 * `WebAppWithRisks[]`. Web apps with no risks still produce a card
 * (`optional: true` on the relationship keeps zero-vuln seeds).
 *
 * AGGREGATION (BUG 1): the `HAS_VULNERABILITY` relationship is `optional: true`,
 * which makes Neo4j return the SAME web-app root once per matched relationship
 * (one risk per row). `transformTreeNodeToFlat` (queryBuilder
 * `extractQueryResults.ts`) handles the identical pattern by flattening then
 * `deduplicateByKey`. We mirror that here: rows are grouped by `webAppKey` and
 * risks are deduped by their own `key`, so a web app with N risks collapses to
 * ONE card carrying the deduped union (not N identical cards). Zero-vuln seeds
 * arrive as a single row with empty relationships and still yield one card.
 *
 * Rows whose key cannot be derived are skipped (cannot attribute a card).
 */
export function adaptRowsToWebApps(rows: unknown[]): WebAppWithRisks[] {
  if (!Array.isArray(rows)) return [];

  // Insertion-ordered so the first row's name/position wins; risks are deduped
  // by their own key (mirrors `deduplicateByKey`). Risks without a string key
  // are always kept (counted), keyed by a unique synthetic token.
  const byWebApp = new Map<
    string,
    {
      name: string;
      assetStatus?: string;
      risksByKey: Map<string, { key?: string; status?: string }>;
    }
  >();
  let syntheticRiskKey = 0;

  for (const raw of rows) {
    if (!isRecord(raw)) continue;

    // Real TreeNode shape: `{ node: { result, relationships } }`.
    const node = isRecord(raw.node) ? raw.node : undefined;
    if (!node) continue;

    const webApp = extractWebAppNode(node.result);
    if (!webApp) continue;

    const webAppKey =
      typeof webApp.key === 'string' && webApp.key.length > 0
        ? webApp.key
        : undefined;
    if (!webAppKey) continue;

    const name =
      typeof webApp.name === 'string' && webApp.name.length > 0
        ? webApp.name
        : typeof webApp.identifier === 'string' && webApp.identifier.length > 0
          ? webApp.identifier
          : webAppKey;

    const assetStatus =
      typeof webApp.status === 'string' && webApp.status.length > 0
        ? webApp.status
        : undefined;

    let entry = byWebApp.get(webAppKey);
    if (!entry) {
      entry = { name, assetStatus, risksByKey: new Map() };
      byWebApp.set(webAppKey, entry);
    }

    for (const risk of extractNestedRisks(node.relationships)) {
      const dedupeKey =
        typeof risk.key === 'string' && risk.key.length > 0
          ? risk.key
          : ` synthetic#${syntheticRiskKey++}`;
      if (!entry.risksByKey.has(dedupeKey)) {
        entry.risksByKey.set(dedupeKey, risk);
      }
    }
  }

  return Array.from(
    byWebApp,
    ([webAppKey, { name, assetStatus, risksByKey }]) => ({
      webAppKey,
      name,
      assetStatus,
      risks: Array.from(risksByKey.values()),
    })
  );
}

/**
 * Pull the representative WebApplication node out of `node.result`. The result
 * is bucketed by entity subtype (`{ webapplications: [...] }`); take the first
 * entry of the first non-empty known bucket.
 */
function extractWebAppNode(
  nodeResult: unknown
): Record<string, unknown> | undefined {
  if (!isRecord(nodeResult)) return undefined;
  for (const bucket of WEB_APP_RESULT_BUCKETS) {
    const value = nodeResult[bucket];
    if (Array.isArray(value) && isRecord(value[0])) return value[0];
  }
  // Canonical fallback (mirrors `transformTreeNodeToFlat`): if the known
  // buckets miss, take the first entry of the first array-valued bucket.
  const firstArrayEntry = Object.values(nodeResult).find(value =>
    Array.isArray(value)
  ) as unknown[] | undefined;
  if (firstArrayEntry && isRecord(firstArrayEntry[0]))
    return firstArrayEntry[0];
  return undefined;
}

/**
 * Pull risk rows off a WebApplication tree node's `relationships[]`: each
 * `HAS_VULNERABILITY` edge carries its risks under `target.result.risks[]`
 * (mirrors `ApplicationTab.tsx` `riskRel.target.result.risks`). Edges with
 * other labels are ignored. Part of the verification surface (see
 * {@link adaptRowsToWebApps}).
 */
function extractNestedRisks(relationships: unknown): Array<{
  key?: string;
  status?: string;
}> {
  if (!Array.isArray(relationships)) return [];

  const risks: Array<{ key?: string; status?: string }> = [];
  for (const rel of relationships) {
    if (!isRecord(rel) || rel.label !== 'HAS_VULNERABILITY') continue;
    const target = isRecord(rel.target) ? rel.target : undefined;
    const targetResult =
      target && isRecord(target.result) ? target.result : undefined;
    const riskRows = targetResult?.risks;
    if (!Array.isArray(riskRows)) continue;
    for (const risk of riskRows) {
      if (!isRecord(risk)) continue;
      risks.push({
        key: typeof risk.key === 'string' ? risk.key : undefined,
        status: typeof risk.status === 'string' ? risk.status : undefined,
      });
    }
  }
  return risks;
}

/** Map a `Risk.status` to its severity tier, or null if not countable. */
function severityFromStatus(
  status: string | undefined
): Exclude<RiskSeverity, RiskSeverity.Exposure> | null {
  // Malformed/empty status (< 2 chars) → skip (same guard as SSCActiveIssues).
  if (!status || status.length < 2) return null;
  // Only DEMONSTRATED risks count. status[0] is the lifecycle state and must be
  // Opened ('O' → "Demonstrated"). Everything else is excluded: Rejected
  // ('D' = Deleted), Accepted ('I'), Resolved ('R' = Remediated), and
  // Detected/Triaged ('T'). This mirrors the main vulnerability table's default
  // "Demonstrated" filter (`status: ['O']`) — a web app whose only criticals
  // are Rejected must not show those criticals on its donut.
  if (status[0] !== RiskStatus.Opened) return null;
  const sev = status[1];
  switch (sev) {
    case RiskSeverity.Critical:
    case RiskSeverity.High:
    case RiskSeverity.Medium:
    case RiskSeverity.Low:
    case RiskSeverity.Info:
      return sev;
    // Exposure ('E') and anything else are not surfaced.
    default:
      return null;
  }
}

/**
 * One server-side aggregation row from the demonstrated-risk group query
 * (`groupBy: ['dns', 'status']` + Neo4j's `count(...)`): "this host/URL has
 * `count` open risks with this 2-char status". Fields are loosely typed
 * (`unknown`) because grouped `fields` come back as a dynamic map — the tally
 * validates each value before use.
 */
export interface DemonstratedRiskGroup {
  fields?: { dns?: unknown; status?: unknown };
  count?: unknown;
}

/**
 * Normalize a URL or bare host string to a lowercase hostname. Strips scheme,
 * path/query/fragment, userinfo, and port. `https://app.example.com/api` and
 * `app.example.com:443` both resolve to `app.example.com`.
 */
export function extractHostname(value: string | undefined): string {
  if (!value) return '';
  const noScheme = value.replace(/^[a-z][a-z0-9+.-]*:\/\//i, '');
  const hostPort = noScheme.split(/[/?#]/, 1)[0];
  const afterUserInfo = hostPort.includes('@')
    ? (hostPort.split('@').pop() ?? hostPort)
    : hostPort;
  // Drop :port, but keep IPv6 brackets intact (no colon-splitting inside []).
  const host = afterUserInfo.startsWith('[')
    ? afterUserInfo
    : afterUserInfo.split(':', 1)[0];
  return host.toLowerCase();
}

/**
 * Canonical join key for a hostname: a leading `www.` is stripped so
 * `www.example.com` and `example.com` (and their http/https URL forms) all
 * land on the SAME card with the combined risk total — a host's www and apex
 * variants are the same web application for this widget. The prefix is only
 * stripped when a dot remains afterwards, so a host that is literally
 * `www.com` is left alone.
 */
export function canonicalHost(host: string): string {
  if (host.startsWith('www.') && host.slice('www.'.length).includes('.')) {
    return host.slice('www.'.length);
  }
  return host;
}

/** Canonical hostname a web-app seed is anchored on, derived from its `#webapplication#<url>` key. */
function webAppHost(webAppKey: string, name: string): string {
  const url = webAppKey.startsWith('#webapplication#')
    ? webAppKey.slice('#webapplication#'.length)
    : webAppKey;
  return canonicalHost(extractHostname(url) || extractHostname(name));
}

/**
 * Pivot server-side `(dns, status) → count` risk groups into per-hostname
 * severity counts. A group's `dns` is the Risk node's `dns` property — either a
 * bare host (`example.com`) or, for URL-keyed risks, a full URL
 * (`https://example.com/Api/Save`); both normalize to the same hostname via
 * {@link extractHostname} + {@link canonicalHost}. Risk keys are
 * `#risk#<dns>#<name>`, so grouping on `dns` loses nothing vs parsing the key.
 * Non-open and Exposure-severity groups are excluded by
 * {@link severityFromStatus}; groups without a usable `dns`/`count` are skipped.
 */
function tallyDemonstratedRisksByHost(
  riskGroups: DemonstratedRiskGroup[]
): Map<string, SeverityCounts> {
  const byHost = new Map<string, SeverityCounts>();
  if (!Array.isArray(riskGroups)) return byHost;
  for (const group of riskGroups) {
    if (!isRecord(group)) continue;
    const fields = isRecord(group.fields) ? group.fields : undefined;
    const status = fields?.status;
    const sev = severityFromStatus(
      typeof status === 'string' ? status : undefined
    );
    if (sev === null) continue;
    const host = canonicalHost(
      extractHostname(typeof fields?.dns === 'string' ? fields.dns : undefined)
    );
    if (!host) continue;
    const count = typeof group.count === 'number' ? group.count : 0;
    if (count <= 0) continue;
    let counts = byHost.get(host);
    if (!counts) {
      counts = emptyCounts();
      byHost.set(host, counts);
    }
    counts[sev] += count;
  }
  return byHost;
}

/**
 * Highest-vuln-count-first comparator: order by `total` (demonstrated vuln
 * count) descending. Ties break most-severe-first on the `[C,H,M,L,I]` tuple,
 * then name asc for a stable order.
 */
function compareCards(a: WebAppSeverityCard, b: WebAppSeverityCard): number {
  if (b.total !== a.total) return b.total - a.total;
  // COUNTED_SEVERITIES is already most-severe-first (Critical → Info), so
  // iterate it directly — no need to rebuild a RiskPriority-sorted order per call.
  for (const sev of COUNTED_SEVERITIES) {
    const diff = b.counts[sev] - a.counts[sev];
    if (diff !== 0) return diff;
  }
  return a.name.localeCompare(b.name);
}

/**
 * PURE. Build one severity-breakdown card per ACTIVE web-application SEED,
 * counting ALL of that host's DEMONSTRATED (open) risks — web-app-origin AND
 * network-origin alike — by joining the seeds to the server-aggregated
 * `(dns, status) → count` risk groups on canonical hostname.
 *
 * Why a host join instead of the seed's `HAS_VULNERABILITY` edges: risks attach
 * to a host's DNS Asset, not to the WebApplication seed node, so the seed's
 * direct relationships miss most of its risks. A host imported as both a
 * network seed and a web-app seed therefore shows its full risk total here.
 *
 * Cards are deduped by canonical hostname (http/https and www/apex variants of
 * one host collapse to a single card), web apps with zero demonstrated risks
 * are dropped (no empty donuts), and the result is sorted highest-vuln-count
 * first.
 *
 * @param seedRows raw tree/flat rows for the WebApplication seeds (shape
 *   dependency isolated in {@link adaptRowsToWebApps})
 * @param riskGroups the tenant's demonstrated (open) risks, pre-aggregated
 *   server-side by `dns` + `status`
 */
export function buildWebAppSeverityCards(
  seedRows: unknown[],
  riskGroups: DemonstratedRiskGroup[]
): WebAppSeverityCard[] {
  // Only ACTIVE web apps get a card — frozen/deleted/pending/unavailable seeds
  // are dropped entirely.
  const webApps = adaptRowsToWebApps(seedRows).filter(webApp =>
    isActiveWebApp(webApp.assetStatus)
  );

  const countsByHost = tallyDemonstratedRisksByHost(riskGroups);

  const cards: WebAppSeverityCard[] = [];
  const seenHosts = new Set<string>();
  for (const { webAppKey, name } of webApps) {
    const host = webAppHost(webAppKey, name);
    // Dedupe by host so http/https (or duplicate) seeds for the same host
    // produce one card; the first seed wins for the key/label + deep-link.
    if (!host || seenHosts.has(host)) continue;
    seenHosts.add(host);

    const counts = countsByHost.get(host) ?? emptyCounts();
    const total = COUNTED_SEVERITIES.reduce((sum, sev) => sum + counts[sev], 0);
    cards.push({ webAppKey, name, counts, total });
  }

  // Drop web apps with no demonstrated vulnerabilities — a wall of all-zero
  // donuts is noise; only surface web apps that actually have open risks.
  return cards.filter(card => card.total > 0).sort(compareCards);
}
